import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient, mapMemberToVoiceTeamMember } from './_shared';
import type { VoiceSettingsTeamMember } from './voiceSettings';

// ── DNC entry management (BOOSTT-3174) — add/remove/search single numbers ──

export type DncEntry = {
  id: string;
  phone: string;
  source: string;
  added_date: string;
  notes?: string | null;
};

export type DncEntriesPage = {
  total: number;
  skip: number;
  limit: number;
  items: DncEntry[];
};

export async function searchDncEntries(
  params: { search?: string; skip?: number; limit?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<DncEntriesPage> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<DncEntriesPage>(
    `${apiConfig.voiceEndpoints.workspaces}/current/dnc`,
    { params },
  );
  return response.data;
}

export async function addDncEntry(
  phone: string, notes?: string, axiosInstance?: AxiosInstance,
): Promise<DncEntry> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<DncEntry>(
    `${apiConfig.voiceEndpoints.workspaces}/current/dnc`,
    { phone, notes: notes || undefined },
  );
  return response.data;
}

export async function removeDncEntry(phone: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.delete(`${apiConfig.voiceEndpoints.workspaces}/current/dnc/${encodeURIComponent(phone)}`);
}

export type InviteVoiceTeamMemberInput = { name: string; email: string; role?: string };

export async function inviteVoiceTeamMember(input: InviteVoiceTeamMemberInput, axiosInstance?: AxiosInstance): Promise<VoiceSettingsTeamMember> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workspaces}/current/invites`;
  const roleRaw = (input.role || 'Member').trim();
  const role = ['Admin', 'Member', 'Viewer'].includes(roleRaw) ? roleRaw : 'Member';
  const response = await client.post<any>(url, { email: input.email.trim(), role });
  const d = response.data;
  if (d && typeof d === 'object' && d.id) return mapMemberToVoiceTeamMember(d);
  const name = input.name.trim();
  return {
    id: `pending-${Date.now()}`,
    name,
    email: input.email.trim(),
    role,
    avatar: name
      .split(/\s+/)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '—',
    lastActive: 'Invited',
  };
}
